import React from "react";
import { 
  Calendar, RefreshCw, Thermometer, Clock,
  Droplets, Wind, Cloud, Sun, Zap, Home
} from "lucide-react";
import { CurrentWeather } from "../../data/weatherService";
import { SiteDetails } from "../../data/sitelocations";
import LocationButton from "./LocationButton";

import { useTheme } from "../ThemeContext";

interface WeatherWidgetProps {
  weatherData: CurrentWeather | null;
  weatherLoading: boolean;
  currentTime: Date;
  siteInfo: SiteDetails | null;
  onHomeClick?: () => void;
  showHomeButton?: boolean;
}

const WeatherWidget: React.FC<WeatherWidgetProps> = ({
  weatherData,
  weatherLoading,
  currentTime,
  siteInfo,
  onHomeClick,
  showHomeButton = false
}) => {
  const { isDarkMode } = useTheme();

  const formatTime = (d: Date) =>
    d.toLocaleTimeString("en-US", { hour12: true, hour: "2-digit", minute: "2-digit" });
  const formatDate = (d: Date) =>
    d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

  const getWeatherIcon = (iconCode?: string | null) => {
    if (!iconCode) return <Cloud className="w-4 h-4 text-gray-400" />;
    if (iconCode.startsWith("01")) return <Sun className="w-4 h-4 text-amber-500" />;
    if (iconCode.startsWith("02")) return <span className="text-[#3EA3DA] text-sm">⛅</span>;
    if (iconCode.startsWith("03") || iconCode.startsWith("04")) return <Cloud className="w-4 h-4 text-gray-500" />;
    if (iconCode.startsWith("09") || iconCode.startsWith("10")) return <span className="text-[#0066A3] text-sm">🌧️</span>;
    if (iconCode.startsWith("11")) return <Zap className="w-4 h-4 text-[#0066A3]" />;
    if (iconCode.startsWith("13")) return <span className="text-[#C2DFF1] text-sm">❄️</span>;
    if (iconCode.startsWith("50")) return <Cloud className="w-4 h-4 text-gray-400" />;
    return <Cloud className="w-4 h-4 text-gray-400" />;
  };

  const parts = (siteInfo?.location || "Bengaluru, Karnataka").split(",").map(s => s.trim());
  const placeCityState = parts.slice(0, 2).join(", ");
  const defaultPlaceName = siteInfo?.displayName || placeCityState || "Plant Location";

  const dividerClass = isDarkMode ? "border-r border-[#1E3A5F]" : "border-r border-[#C2DFF1]/50";
  const segmentBg = isDarkMode ? "bg-slate-800/80" : "bg-white/70";

  // Always show widget with at least date/time, show weather if available
  return (
    <div 
      className={`group weather-widget-capsule inline-flex items-stretch h-10 rounded-2xl ${
        isDarkMode 
          ? "bg-slate-900/95 border border-[#1E3A5F] shadow-lg" 
          : "bg-white/95 border border-[#C2DFF1] shadow"
      } transition-all duration-300 min-w-0`}
      title={weatherData ? `${weatherData.description} • ${placeCityState}` : `${defaultPlaceName} • ${placeCityState}`}
      style={{ overflow: 'visible' }}
    >
      {/* Home Button Icon - Left side */}
      {showHomeButton && onHomeClick && (
        <button
          onClick={onHomeClick}
          className={`flex items-center justify-center pl-4 pr-3 ${dividerClass} ${segmentBg} rounded-l-2xl hover:bg-white/90 dark:hover:bg-slate-700/80 transition-colors`}
          title="Home"
        >
          <Home size={18} className="text-[#0084CF] hover:text-[#0066A3] dark:text-sky-400" />
        </button>
      )}
      
      {/* Loading Indicator (Subtle) */}
      {weatherLoading && (
        <div className={`flex items-center justify-center pl-3 pr-2 ${dividerClass} ${segmentBg} ${!showHomeButton ? 'rounded-l-2xl' : ''}`}>
          <RefreshCw className="w-4 h-4 animate-spin text-[#3EA3DA]" />
        </div>
      )}

      {/* Temperature / Weather Status */}
      {weatherData ? (
        <div className={`flex items-center gap-2 pl-4 pr-4 ${dividerClass} ${segmentBg} ${!showHomeButton && !weatherLoading ? 'rounded-l-2xl' : ''}`}>
          <div className="relative">
            {getWeatherIcon(weatherData.icon)}
            <span className="absolute -top-1 -right-1 block h-2 w-2 rounded-full bg-amber-400/80" />
          </div>
          <span className="text-sm header-light-text text-gray-900 dark:text-slate-100" style={{ fontWeight: '300 !important', fontFamily: 'IBM Plex Sans, sans-serif !important' }}>{weatherData.temperature}°</span>
        </div>
      ) : (
        <div className={`flex items-center gap-2 pl-4 pr-4 ${dividerClass} ${segmentBg} ${!showHomeButton && !weatherLoading ? 'rounded-l-2xl' : ''}`}>
          <Thermometer className="w-4 h-4 text-gray-400 dark:text-slate-400" />
          <span className="text-sm header-light-text text-gray-500 dark:text-slate-400" style={{ fontWeight: '300 !important', fontFamily: 'IBM Plex Sans, sans-serif !important' }}>{weatherLoading ? '--°' : 'Weather N/A'}</span>
        </div>
      )}

      {/* Humidity & Wind - Only show if weather data available */}
      {weatherData && (
        <div className={`flex items-center gap-3 px-4 ${dividerClass} ${segmentBg}`}>
          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#F2F8FC] dark:bg-slate-700/70 text-[#1C4D88] dark:text-sky-300 text-sm header-light-text" style={{ fontWeight: '300 !important', fontFamily: 'IBM Plex Sans, sans-serif !important' }}>
            <Droplets className="w-4 h-4 text-[#0084CF] dark:text-sky-400" />
            {weatherData.humidity ?? "—"}%
          </span>
          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 text-sm header-light-text" style={{ fontWeight: '300 !important', fontFamily: 'IBM Plex Sans, sans-serif !important' }}>
            <Wind className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            {weatherData.windSpeed ?? "—"}m/s
          </span>
        </div>
      )}

      {/* Date & Time - Always visible */}
      <div className={`flex items-center gap-3 px-4 ${dividerClass} ${segmentBg}`}>
        <div className="flex items-center gap-1.5 text-sm text-gray-800 dark:text-slate-200">
          <Calendar className="w-4 h-4 text-[#0084CF] dark:text-sky-400" />
          <span className="header-light-text" style={{ fontWeight: '300 !important', fontFamily: 'IBM Plex Sans, sans-serif !important' }}>{formatDate(currentTime)}</span>
        </div>
        <div className="flex items-center gap-1.5 text-sm text-gray-800 dark:text-slate-200">
          <Clock className="w-4 h-4 text-[#0084CF] dark:text-sky-400" />
          <span className="header-light-text" style={{ fontWeight: '300 !important', fontFamily: 'IBM Plex Sans, sans-serif !important' }}>{formatTime(currentTime)}</span>
        </div>
      </div>

      {/* Location Button */}
      <LocationButton 
        placeName={placeCityState || defaultPlaceName}
        siteInfo={siteInfo}
      />
    </div>
  );
};

export default WeatherWidget;
